import type { SmepEventConfig } from '@nativewaves/nw-cloud-sdk/smep'

import type {
  ConfigFormValues,
  GlobalAudioRow,
  NewId,
  SmepAudioTrack,
  SmepEntity,
  SmepGlobalAudio,
  SmepVideoTrack,
  ViewRow,
} from './types'

/*
 * Pure config <-> form model. NO runtime imports (SDK types are `import type`
 * only, the id generator is injected) so this module can be exercised directly
 * under `node --experimental-strip-types` against real example configs.
 *
 * Invariant (no-clobber): serialize only ever (a) edits entity/global-audio
 * name+disable, (b) rewrites a *private* track's stream, (c) appends new private
 * tracks, or (d) re-points a link. It never rewrites or deletes a track it does
 * not exclusively own; every unsupported top-level and per-item field passes
 * through by identity.
 */

const indexById = <T extends { id: string }>(items: readonly T[]): Map<string, T> => {
  const map = new Map<string, T>()
  for (const item of items) {
    map.set(item.id, item)
  }
  return map
}

/** Number inputs are momentarily empty (NaN) while typing — never persist NaN. */
const finiteOr0 = (value: number): number => (Number.isFinite(value) ? value : 0)

// ---------------------------------------------------------------------------
// Parse: config -> form rows
// ---------------------------------------------------------------------------

const parseViewRow = (entity: SmepEntity, tracksById: Map<string, SmepVideoTrack>): ViewRow => {
  const contents = entity.videoContents ?? []
  const singleContent = contents.length === 1 ? contents[0] : undefined
  const links = singleContent?.videoLinks ?? []
  const singleLink = links.length === 1 ? links[0] : undefined
  const trackId = singleLink?.trackId
  const track = trackId != null ? tracksById.get(trackId) : undefined
  const streamSources = track?.streamSources ?? []

  const streamEditable =
    singleContent != null &&
    singleLink != null &&
    track != null &&
    !track.detached &&
    track.source == null &&
    (track.externalSources?.length ?? 0) === 0 &&
    (track.objects?.length ?? 0) === 0 &&
    streamSources.length <= 1

  return {
    entityId: entity.id,
    name: entity.name ?? '',
    enabled: !entity.disable,
    trackId,
    streamId: streamSources[0]?.streamId,
    streamEditable,
  }
}

const parseGlobalAudioRow = (
  globalAudio: SmepGlobalAudio,
  audioTracksById: Map<string, SmepAudioTrack>,
): GlobalAudioRow => {
  const links = globalAudio.audioLinks ?? []
  const singleLink = links.length === 1 ? links[0] : undefined
  const trackId = singleLink?.trackId
  const track = trackId != null ? audioTracksById.get(trackId) : undefined
  const source = track?.source

  const streamEditable =
    singleLink != null &&
    track != null &&
    !track.detached &&
    source?.type === 'Stream' &&
    source.stream != null &&
    (track.streamSources?.length ?? 0) === 0 &&
    (track.externalSources?.length ?? 0) === 0 &&
    (track.objects?.length ?? 0) === 0

  return {
    globalAudioId: globalAudio.id,
    name: globalAudio.name ?? '',
    enabled: !globalAudio.disable,
    audioTrackId: trackId,
    streamId: source?.stream?.streamId,
    streamEditable,
  }
}

/** `extensions.custom.statscoreFixtureInfo.fixtureId` (free-form) — read as a plain string. */
export const readFixtureId = (config: SmepEventConfig): string =>
  (config.extensions?.custom?.statscoreFixtureInfo?.fixtureId as string | undefined) ?? ''

export const parseConfig = (config: SmepEventConfig): ConfigFormValues => {
  const tracksById = indexById(config.videoTracks ?? [])
  const audioTracksById = indexById(config.audioTracks ?? [])
  return {
    viewRows: (config.entities ?? []).map((entity) => parseViewRow(entity, tracksById)),
    globalAudioRows: (config.globalAudios ?? []).map((globalAudio) =>
      parseGlobalAudioRow(globalAudio, audioTracksById),
    ),
    name: config.name ?? '',
    tags: Array.from(config.tags ?? []),
    liveDelayStrategy: config.liveDelayStrategy,
    pointZeroRealTimeReferenceShift: config.pointZeroRealTimeReferenceShift ?? 0,
    fixtureId: readFixtureId(config),
  }
}

// ---------------------------------------------------------------------------
// Ref-counting + "managed" (privately-owned, safe-to-rewrite) track detection
// ---------------------------------------------------------------------------

const videoTrackRefCounts = (config: SmepEventConfig): Map<string, number> => {
  const counts = new Map<string, number>()
  for (const entity of config.entities ?? []) {
    for (const content of entity.videoContents ?? []) {
      for (const link of content.videoLinks ?? []) {
        if (link.trackId != null) {
          counts.set(link.trackId, (counts.get(link.trackId) ?? 0) + 1)
        }
      }
    }
  }
  return counts
}

const audioTrackRefCounts = (config: SmepEventConfig): Map<string, number> => {
  const counts = new Map<string, number>()
  const countLinks = (links: SmepGlobalAudio['audioLinks'] | undefined) => {
    for (const link of links ?? []) {
      if (link.trackId != null) {
        counts.set(link.trackId, (counts.get(link.trackId) ?? 0) + 1)
      }
    }
  }
  for (const globalAudio of config.globalAudios ?? []) {
    countLinks(globalAudio.audioLinks)
  }
  // Audio tracks can also be referenced by entity audioContents — count those too
  // so a track shared with an entity is never treated as privately owned.
  for (const entity of config.entities ?? []) {
    for (const content of entity.audioContents ?? []) {
      countLinks(content.audioLinks)
    }
  }
  return counts
}

const managedVideoTrackIds = (config: SmepEventConfig): Set<string> => {
  const counts = videoTrackRefCounts(config)
  const tracksById = indexById(config.videoTracks ?? [])
  const managed = new Set<string>()
  for (const entity of config.entities ?? []) {
    const row = parseViewRow(entity, tracksById)
    if (row.streamEditable && row.trackId != null && counts.get(row.trackId) === 1) {
      managed.add(row.trackId)
    }
  }
  return managed
}

const managedAudioTrackIds = (config: SmepEventConfig): Set<string> => {
  const counts = audioTrackRefCounts(config)
  const audioTracksById = indexById(config.audioTracks ?? [])
  const managed = new Set<string>()
  for (const globalAudio of config.globalAudios ?? []) {
    const row = parseGlobalAudioRow(globalAudio, audioTracksById)
    if (row.streamEditable && row.audioTrackId != null && counts.get(row.audioTrackId) === 1) {
      managed.add(row.audioTrackId)
    }
  }
  return managed
}

// ---------------------------------------------------------------------------
// Build factories (shapes taken from real example configs)
// ---------------------------------------------------------------------------

const buildStreamSource = (streamId: string) => ({
  disable: false,
  streamId,
  shift: 0,
  ignoreAvcTaskAssociations: false,
  liveOnly: false,
})

const buildNewVideoTrack = (id: string, streamId: string | undefined): SmepVideoTrack => ({
  id,
  disable: false,
  detached: false,
  name: '',
  localizedNames: null,
  streamSources: streamId != null ? [buildStreamSource(streamId)] : [],
  externalSources: [],
  unavailabilities: [],
  objects: [],
})

const buildNewEntity = (row: ViewRow, trackId: string, newId: NewId): SmepEntity => ({
  id: row.entityId,
  disable: !row.enabled,
  name: row.name,
  localizedNames: null,
  videoContents: [
    {
      id: newId(),
      disable: false,
      name: '',
      localizedNames: null,
      thumbnail: {
        overrideUrl: null,
        autoLoadConfig: { enabled: true, position: 0, usePositionForLive: false, usePositionForEnded: false },
      },
      videoLinks: [{ id: newId(), disable: false, trackId, unavailabilities: [] }],
    },
  ],
  audioContents: [],
  labelIds: [],
  extensions: { custom: {} },
})

/** Editable existing entity: keep structure/unsupported subfields, update name/disable/link target. */
const rebuildEntity = (orig: SmepEntity, row: ViewRow, trackId: string, newId: NewId): SmepEntity => {
  const origContent = orig.videoContents?.[0]
  const origLink = origContent?.videoLinks?.[0]
  const videoLinks = [
    origLink ? { ...origLink, trackId } : { id: newId(), disable: false, trackId, unavailabilities: [] },
    ...(origContent?.videoLinks?.slice(1) ?? []),
  ]
  const content = origContent
    ? { ...origContent, videoLinks }
    : { id: newId(), disable: false, name: '', localizedNames: null, videoLinks }
  return {
    ...orig,
    name: row.name,
    disable: !row.enabled,
    videoContents: [content, ...(orig.videoContents?.slice(1) ?? [])],
  }
}

/** Advanced (non-representable) entity: only touch name/disable, preserve everything else. */
const advancedEntity = (orig: SmepEntity, row: ViewRow): SmepEntity => ({
  ...orig,
  name: row.name,
  disable: !row.enabled,
})

const rewriteVideoTrackStream = (orig: SmepVideoTrack, streamId: string | undefined): SmepVideoTrack => {
  if (streamId == null) {
    return orig
  }
  const first = orig.streamSources?.[0]
  return {
    ...orig,
    streamSources: [{ ...buildStreamSource(streamId), ...first, streamId }, ...(orig.streamSources?.slice(1) ?? [])],
  }
}

const buildNewAudioTrack = (id: string, streamId: string | undefined): SmepAudioTrack => ({
  id,
  disable: false,
  detached: false,
  name: '',
  localizedNames: [],
  source: streamId != null ? { type: 'Stream', stream: buildStreamSource(streamId) } : undefined,
  streamSources: [],
  externalSources: [],
  unavailabilities: [],
  objects: [],
})

const buildNewGlobalAudio = (row: GlobalAudioRow, trackId: string, newId: NewId): SmepGlobalAudio => ({
  id: row.globalAudioId,
  disable: !row.enabled,
  name: row.name,
  localizedNames: [],
  locales: [],
  audioLinks: [{ id: newId(), disable: false, trackId, unavailabilities: [] }],
})

const rebuildGlobalAudio = (
  orig: SmepGlobalAudio,
  row: GlobalAudioRow,
  trackId: string,
  newId: NewId,
): SmepGlobalAudio => {
  const origLink = orig.audioLinks?.[0]
  return {
    ...orig,
    name: row.name,
    disable: !row.enabled,
    audioLinks: [
      origLink ? { ...origLink, trackId } : { id: newId(), disable: false, trackId, unavailabilities: [] },
      ...(orig.audioLinks?.slice(1) ?? []),
    ],
  }
}

const advancedGlobalAudio = (orig: SmepGlobalAudio, row: GlobalAudioRow): SmepGlobalAudio => ({
  ...orig,
  name: row.name,
  disable: !row.enabled,
})

const rewriteAudioTrackStream = (orig: SmepAudioTrack, streamId: string | undefined): SmepAudioTrack => {
  if (streamId == null) {
    return orig
  }
  const origStream = orig.source?.stream
  return {
    ...orig,
    source: { ...orig.source, type: 'Stream', stream: { ...buildStreamSource(streamId), ...origStream, streamId } },
  }
}

// ---------------------------------------------------------------------------
// Serialize: form rows + fresh base -> config (two-pass per media kind)
// ---------------------------------------------------------------------------

const assembleTracks = <T extends { id: string }>(
  originals: readonly T[],
  rewrites: Map<string, T>,
  managed: Set<string>,
  appended: readonly T[],
): T[] => {
  const result: T[] = []
  for (const track of originals) {
    const rewrite = rewrites.get(track.id)
    if (rewrite) {
      result.push(rewrite)
    } else if (managed.has(track.id)) {
      // Privately owned but no rewrite produced => its owning row was deleted => drop.
      continue
    } else {
      // Shared / advanced / orphan / unmanaged — preserve untouched.
      result.push(track)
    }
  }
  for (const track of appended) {
    result.push(track)
  }
  return result
}

const serializeVideo = (
  config: SmepEventConfig,
  viewRows: readonly ViewRow[],
  newId: NewId,
): { entities: SmepEntity[]; videoTracks: SmepVideoTrack[] } => {
  const entitiesById = indexById(config.entities ?? [])
  const tracksById = indexById(config.videoTracks ?? [])
  const refCounts = videoTrackRefCounts(config)
  const managed = managedVideoTrackIds(config)

  const entities: SmepEntity[] = []
  const rewrites = new Map<string, SmepVideoTrack>()
  const appended: SmepVideoTrack[] = []

  for (const row of viewRows) {
    const orig = entitiesById.get(row.entityId)

    if (!row.streamEditable) {
      entities.push(orig ? advancedEntity(orig, row) : buildNewEntity(row, row.trackId ?? newId(), newId))
      continue
    }

    if (!orig) {
      const trackId = row.trackId ?? newId()
      appended.push(buildNewVideoTrack(trackId, row.streamId))
      entities.push(buildNewEntity(row, trackId, newId))
      continue
    }

    const trackId = row.trackId
    const origTrack = trackId != null ? tracksById.get(trackId) : undefined

    if (trackId != null && managed.has(trackId) && origTrack) {
      rewrites.set(trackId, rewriteVideoTrackStream(origTrack, row.streamId))
      entities.push(rebuildEntity(orig, row, trackId, newId))
    } else if (trackId != null && (refCounts.get(trackId) ?? 0) > 1) {
      // Shared track: copy-on-write only when the stream actually changed.
      if (row.streamId === origTrack?.streamSources?.[0]?.streamId) {
        entities.push(rebuildEntity(orig, row, trackId, newId))
      } else {
        const newTrackId = newId()
        appended.push(buildNewVideoTrack(newTrackId, row.streamId))
        entities.push(rebuildEntity(orig, row, newTrackId, newId))
      }
    } else if (trackId != null) {
      // Unmanaged, not shared (defensive): keep the link, only edit name/disable.
      entities.push(rebuildEntity(orig, row, trackId, newId))
    } else {
      // Editable but link missing: give it a fresh private track.
      const newTrackId = newId()
      appended.push(buildNewVideoTrack(newTrackId, row.streamId))
      entities.push(rebuildEntity(orig, row, newTrackId, newId))
    }
  }

  return { entities, videoTracks: assembleTracks(config.videoTracks ?? [], rewrites, managed, appended) }
}

const serializeAudio = (
  config: SmepEventConfig,
  rows: readonly GlobalAudioRow[],
  newId: NewId,
): { globalAudios: SmepGlobalAudio[]; audioTracks: SmepAudioTrack[] } => {
  const globalAudiosById = indexById(config.globalAudios ?? [])
  const tracksById = indexById(config.audioTracks ?? [])
  const refCounts = audioTrackRefCounts(config)
  const managed = managedAudioTrackIds(config)

  const globalAudios: SmepGlobalAudio[] = []
  const rewrites = new Map<string, SmepAudioTrack>()
  const appended: SmepAudioTrack[] = []

  for (const row of rows) {
    const orig = globalAudiosById.get(row.globalAudioId)

    if (!row.streamEditable) {
      globalAudios.push(
        orig ? advancedGlobalAudio(orig, row) : buildNewGlobalAudio(row, row.audioTrackId ?? newId(), newId),
      )
      continue
    }

    if (!orig) {
      const trackId = row.audioTrackId ?? newId()
      appended.push(buildNewAudioTrack(trackId, row.streamId))
      globalAudios.push(buildNewGlobalAudio(row, trackId, newId))
      continue
    }

    const trackId = row.audioTrackId
    const origTrack = trackId != null ? tracksById.get(trackId) : undefined

    if (trackId != null && managed.has(trackId) && origTrack) {
      rewrites.set(trackId, rewriteAudioTrackStream(origTrack, row.streamId))
      globalAudios.push(rebuildGlobalAudio(orig, row, trackId, newId))
    } else if (trackId != null && (refCounts.get(trackId) ?? 0) > 1) {
      if (row.streamId === origTrack?.source?.stream?.streamId) {
        globalAudios.push(rebuildGlobalAudio(orig, row, trackId, newId))
      } else {
        const newTrackId = newId()
        appended.push(buildNewAudioTrack(newTrackId, row.streamId))
        globalAudios.push(rebuildGlobalAudio(orig, row, newTrackId, newId))
      }
    } else if (trackId != null) {
      globalAudios.push(rebuildGlobalAudio(orig, row, trackId, newId))
    } else {
      const newTrackId = newId()
      appended.push(buildNewAudioTrack(newTrackId, row.streamId))
      globalAudios.push(rebuildGlobalAudio(orig, row, newTrackId, newId))
    }
  }

  return { globalAudios, audioTracks: assembleTracks(config.audioTracks ?? [], rewrites, managed, appended) }
}

export const serializeConfig = (config: SmepEventConfig, values: ConfigFormValues, newId: NewId): SmepEventConfig => {
  const { entities, videoTracks } = serializeVideo(config, values.viewRows, newId)
  const { globalAudios, audioTracks } = serializeAudio(config, values.globalAudioRows, newId)

  // Only touch extensions when the fixture id actually changed, so untouched configs
  // round-trip byte-identically and every other `custom` key (e.g. playback) survives.
  const extensions =
    values.fixtureId === readFixtureId(config)
      ? config.extensions
      : {
          ...config.extensions,
          custom: {
            ...config.extensions?.custom,
            statscoreFixtureInfo: {
              ...(config.extensions?.custom?.statscoreFixtureInfo ?? {}),
              fixtureId: values.fixtureId,
            },
          },
        }

  return {
    ...config,
    name: values.name,
    // `tags` is typed `Set<string>` by the SDK but is a plain array at runtime
    // (as the raw JSON editor round-trips it); send an array.
    tags: values.tags as unknown as SmepEventConfig['tags'],
    liveDelayStrategy: {
      ...values.liveDelayStrategy,
      realTimeDelay: finiteOr0(values.liveDelayStrategy.realTimeDelay),
      playbackDelay: finiteOr0(values.liveDelayStrategy.playbackDelay),
    },
    pointZeroRealTimeReferenceShift: finiteOr0(values.pointZeroRealTimeReferenceShift),
    extensions,
    entities,
    videoTracks,
    globalAudios,
    audioTracks,
  }
}
